"use client";

import { useState, useEffect } from "react";
import {
    Button,
    Table,
    Tag,
    Drawer,
    Form,
    Input,
    Select,
    Spin,
    message,
    Space,
    Popconfirm,
    InputNumber,
    DatePicker,
    Switch,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import useAnnouncementsStore, { Announcement } from "./AnnouncementsStore.ts";

const { TextArea } = Input;
const { Option } = Select;

export default function Announcements() {
    const {
        getAnnouncements,
        getAnnouncementById,
        createAnnouncement,
        updateAnnouncement,
        deleteAnnouncement,
        announcementsData,
        announcementData,
        isListLoading,
        isDetailLoading,
        isSaving,
    } = useAnnouncementsStore();

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [searchText, setSearchText] = useState("");
    const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchAnnouncements();
    }, [currentPage, pageSize, searchText, typeFilter, statusFilter]);

    const fetchAnnouncements = () => {
        const params: any = {
            page: currentPage,
            per_page: pageSize,
        };
        if (searchText) {
            params.search = searchText;
        }
        if (typeFilter) {
            params.type = typeFilter;
        }
        if (statusFilter) {
            params.status = statusFilter;
        }
        getAnnouncements(params);
    };

    const handleTableChange = (pagination: TablePaginationConfig) => {
        setCurrentPage(pagination.current || 1);
        setPageSize(pagination.pageSize || 30);
    };

    const handleSearch = (value: string) => {
        setSearchText(value);
        setCurrentPage(1);
    };

    const handleAddAnnouncement = () => {
        setIsEditMode(false);
        form.resetFields();
        setIsDrawerOpen(true);
    };

    const handleEditAnnouncement = async (id: number) => {
        setIsEditMode(true);
        setIsDrawerOpen(true);
        form.resetFields();
        const result = await getAnnouncementById(id);
        if (result.success && result.data) {
            const data = result.data.data;
            // Convert ISO date strings to dayjs objects for DatePicker
            form.setFieldsValue({
                ...data,
                start_at: data.start_at ? dayjs(data.start_at) : null,
                end_at: data.end_at ? dayjs(data.end_at) : null,
            });
        } else {
            message.error("Failed to load announcement details");
        }
    };

    const handleDeleteAnnouncement = async (id: number) => {
        const result = await deleteAnnouncement(id);
        if (result.success) {
            fetchAnnouncements();
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            // Convert dayjs objects to ISO strings
            const payload: any = {
                title: values.title,
                content: values.content,
                type: values.type,
                position: values.position,
                status: values.status,
                priority: values.priority || 0,
            };

            // Handle date fields - convert to ISO string or null
            if (values.start_at) {
                payload.start_at = values.start_at.toISOString();
            } else {
                payload.start_at = null;
            }

            if (values.end_at) {
                payload.end_at = values.end_at.toISOString();
            } else {
                payload.end_at = null;
            }

            let result;
            if (isEditMode && announcementData?.data?.id) {
                result = await updateAnnouncement(announcementData.data.id, payload);
            } else {
                result = await createAnnouncement(payload);
            }

            if (result.success) {
                setIsDrawerOpen(false);
                fetchAnnouncements();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const columns: ColumnsType<Announcement> = [
        {
            title: (
                <span className="flex items-center gap-2">
                    <i className="fa-solid fa-bullhorn text-black"></i>
                    <span className="font-semibold">Title</span>
                </span>
            ),
            dataIndex: "title",
            key: "title",
            render: (text) => (
                <span className="font-medium text-gray-900">{text}</span>
            ),
        },
        {
            title: (
                <span className="flex items-center gap-2">
                    <i className="fa-solid fa-tag text-black"></i>
                    <span className="font-semibold">Type</span>
                </span>
            ),
            dataIndex: "type",
            key: "type",
            render: (type: string) => {
                const colors: Record<string, string> = {
                    banner: "blue",
                    popup: "purple",
                    text: "green",
                };
                return <Tag color={colors[type] || "default"}>{type}</Tag>;
            },
        },
        {
            title: (
                <span className="flex items-center gap-2">
                    <i className="fa-solid fa-map-marker-alt text-black"></i>
                    <span className="font-semibold">Position</span>
                </span>
            ),
            dataIndex: "position",
            key: "position",
            render: (position: string) => (
                <Tag color="orange">{position}</Tag>
            ),
        },
        {
            title: (
                <span className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-black"></i>
                    <span className="font-semibold">Status</span>
                </span>
            ),
            dataIndex: "status",
            key: "status",
            render: (status: string) => (
                <Tag color={status === "active" ? "success" : "error"}>
                    {status === "active" ? "Active" : "Inactive"}
                </Tag>
            ),
        },
        {
            title: (
                <span className="flex items-center gap-2">
                    <i className="fa-solid fa-star text-black"></i>
                    <span className="font-semibold">Priority</span>
                </span>
            ),
            dataIndex: "priority",
            key: "priority",
            align: "center",
            render: (priority: number) => (
                <span className="font-medium">{priority}</span>
            ),
        },
        {
            title: (
                <span className="flex items-center gap-2">
                    <i className="fa-solid fa-calendar text-black"></i>
                    <span className="font-semibold">Time Range</span>
                </span>
            ),
            key: "time_range",
            render: (_, record) => {
                const start = record.start_at ? dayjs(record.start_at).format("DD/MM/YYYY") : "No limit";
                const end = record.end_at ? dayjs(record.end_at).format("DD/MM/YYYY") : "No limit";
                return (
                    <div className="text-xs text-gray-600">
                        <div>From: {start}</div>
                        <div>To: {end}</div>
                    </div>
                );
            },
        },
        {
            title: <span className="font-semibold">Actions</span>,
            key: "action",
            align: "center",
            width: 160,
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<i className="fa-solid fa-pen-to-square"></i>}
                        type="text"
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => handleEditAnnouncement(record.id)}
                    />
                    <Popconfirm
                        title="Delete this announcement?"
                        description="Are you sure to delete this announcement?"
                        onConfirm={() => handleDeleteAnnouncement(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            icon={<i className="fa-solid fa-trash"></i>}
                            type="text"
                            danger
                            className="hover:bg-red-50"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const paginationConfig = {
        current: announcementsData?.current_page || 1,
        pageSize: announcementsData?.per_page || 30,
        total: announcementsData?.total || 0,
        showSizeChanger: true,
        showTotal: (total: number, range: [number, number]) =>
            `${range[0]}-${range[1]} of ${total} announcements`,
    };

    return (
        <>
            <div className="mb-8">
                <div className="flex items-center gap-2 text-sm text-gray-400 mb-2 font-medium">
                    <span>Home</span>
                    <span>/</span>
                    <span className="text-black">Announcements</span>
                </div>
                <h1 className="text-3xl font-bold text-black">Announcements Management</h1>
            </div>

            <div className="bg-white rounded-3xl shadow-soft border border-gray-100 overflow-hidden">
                <div className="p-6">
                    <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center text-primary-600">
                                <i className="fa-solid fa-bullhorn text-black text-lg"></i>
                            </div>
                            <h3 className="text-lg font-bold text-black m-0">All Announcements</h3>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <Input.Search
                                placeholder="Search announcements..."
                                onSearch={handleSearch}
                                style={{ width: 250 }}
                                className="rounded-xl"
                                prefix={<i className="fa-solid fa-magnifying-glass text-gray-400"></i>}
                            />
                            <Select
                                placeholder="Filter by type"
                                allowClear
                                style={{ width: 150 }}
                                value={typeFilter}
                                onChange={(value) => {
                                    setTypeFilter(value);
                                    setCurrentPage(1);
                                }}
                            >
                                <Option value="banner">Banner</Option>
                                <Option value="popup">Popup</Option>
                                <Option value="text">Text</Option>
                            </Select>
                            <Select
                                placeholder="Filter by status"
                                allowClear
                                style={{ width: 150 }}
                                value={statusFilter}
                                onChange={(value) => {
                                    setStatusFilter(value);
                                    setCurrentPage(1);
                                }}
                            >
                                <Option value="active">Active</Option>
                                <Option value="inactive">Inactive</Option>
                            </Select>
                            <Button
                                type="primary"
                                icon={<i className="fa-solid fa-plus text-black"></i>}
                                className="shadow-lg shadow-yellow-200/50"
                                onClick={handleAddAnnouncement}
                            >
                                Add New Announcement
                            </Button>
                        </div>
                    </div>
                    <Table
                        columns={columns}
                        dataSource={announcementsData?.data}
                        loading={isListLoading}
                        rowKey="id"
                        pagination={paginationConfig}
                        onChange={handleTableChange}
                        scroll={{ x: 1200 }}
                    />
                </div>
            </div>

            <Drawer
                title={
                    <span className="flex items-center gap-2 text-lg">
                        <i className="fa-solid fa-bullhorn text-black"></i>
                        {isEditMode ? "Edit Announcement" : "New Announcement"}
                    </span>
                }
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                width={600}
                destroyOnClose
                footer={
                    <div className="flex justify-end gap-2">
                        <Button onClick={() => setIsDrawerOpen(false)}>Cancel</Button>
                        <Button type="primary" onClick={handleSave} loading={isSaving}>
                            Save
                        </Button>
                    </div>
                }
            >
                {isDetailLoading ? (
                    <div className="flex justify-center items-center h-40">
                        <Spin tip="Loading..." />
                    </div>
                ) : (
                    <Form form={form} layout="vertical" className="pt-4">
                        <Form.Item
                            name="title"
                            label="Title"
                            rules={[
                                { required: true, message: "Please enter title" },
                                { max: 255, message: "Title must not exceed 255 characters" },
                            ]}
                        >
                            <Input placeholder="Enter announcement title" maxLength={255} />
                        </Form.Item>

                        <Form.Item
                            name="content"
                            label="Content"
                            rules={[{ required: true, message: "Please enter content" }]}
                        >
                            <TextArea
                                rows={6}
                                placeholder="Enter announcement content..."
                                showCount
                            />
                        </Form.Item>

                        <div className="grid grid-cols-2 gap-4">
                            <Form.Item
                                name="type"
                                label="Type"
                                rules={[{ required: true, message: "Please select type" }]}
                            >
                                <Select placeholder="Select type">
                                    <Option value="banner">Banner</Option>
                                    <Option value="popup">Popup</Option>
                                    <Option value="text">Text</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="position"
                                label="Position"
                                rules={[{ required: true, message: "Please select position" }]}
                                initialValue="home"
                            >
                                <Select placeholder="Select position">
                                    <Option value="home">Home</Option>
                                </Select>
                            </Form.Item>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <Form.Item
                                name="status"
                                label="Status"
                                rules={[{ required: true, message: "Please select status" }]}
                                initialValue="active"
                            >
                                <Select placeholder="Select status">
                                    <Option value="active">Active</Option>
                                    <Option value="inactive">Inactive</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="priority"
                                label="Priority"
                                rules={[{ type: "number", min: 0, message: "Priority must be >= 0" }]}
                                initialValue={0}
                            >
                                <InputNumber
                                    min={0}
                                    className="w-full"
                                    placeholder="Priority (higher = more important)"
                                />
                            </Form.Item>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <Form.Item
                                name="start_at"
                                label="Start Date"
                                tooltip="Leave empty for no start limit"
                            >
                                <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm:ss"
                                    className="w-full"
                                    placeholder="Select start date"
                                />
                            </Form.Item>

                            <Form.Item
                                name="end_at"
                                label="End Date"
                                tooltip="Leave empty for no end limit"
                                dependencies={["start_at"]}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            const startAt = getFieldValue("start_at");
                                            if (!value || !startAt) {
                                                return Promise.resolve();
                                            }
                                            if (value.isBefore(startAt)) {
                                                return Promise.reject(
                                                    new Error("End date must be after start date")
                                                );
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm:ss"
                                    className="w-full"
                                    placeholder="Select end date"
                                />
                            </Form.Item>
                        </div>
                    </Form>
                )}
            </Drawer>
        </>
    );
}

